import { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Copy, Check, Users, Play, RotateCcw } from 'lucide-react'
import {
  subscribeToSession,
  subscribeToParticipants,
  startSession,
} from '../../lib/session'
import { useAuth } from '../auth/AuthProvider'
import { authHeaders } from '../../lib/auth'
import type { SessionMetadata, Participant, Stage } from '../../types'
import { STAGE_NAMES, STAGE_AGENTS, AGENT_DISPLAY_NAMES } from '../../types'
import { FUNCTIONS_BASE_URL } from '../../lib/functionsBase'


export default function LobbyPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()

  const { user, loading: authLoading } = useAuth()
  const [session, setSession] = useState<SessionMetadata | null>(null)
  const [participants, setParticipants] = useState<Participant[]>([])
  const [copied, setCopied] = useState(false)
  const [starting, setStarting] = useState(false)
  const summaryTriggered = useRef(false)

  const participantId = sessionStorage.getItem('participantId')
  // Use Firebase auth UID for creator check (survives browser restarts)
  const isCreator = user?.uid
    ? session?.creatorUid === user.uid
    : session?.createdBy === participantId

  // Recover participantId from session data when sessionStorage is empty
  useEffect(() => {
    if (participantId || !session || !user?.uid) return
    if (session.creatorUid === user.uid && session.createdBy) {
      sessionStorage.setItem('participantId', session.createdBy)
      const p = participants.find(p => p.id === session.createdBy)
      if (p) sessionStorage.setItem('displayName', p.displayName)
    }
  }, [session, participants, user?.uid, participantId])

  // Subscribe to session metadata
  useEffect(() => {
    if (!sessionId) return
    const unsub = subscribeToSession(sessionId, (data) => {
      setSession(data)
    })
    return unsub
  }, [sessionId])

  // Navigate to session when active (after participant recovery has a chance to run)
  useEffect(() => {
    if (!session || !sessionId || session.status !== 'active') return
    const storedPid = sessionStorage.getItem('participantId')
    if (storedPid) {
      navigate(`/session/${sessionId}`)
      return
    }
    // If no participantId yet but we can recover from auth, wait for recovery
    if (user?.uid && session.creatorUid === user.uid && session.createdBy) {
      sessionStorage.setItem('participantId', session.createdBy)
      const p = participants.find(p => p.id === session.createdBy)
      if (p) sessionStorage.setItem('displayName', p.displayName)
      navigate(`/session/${sessionId}`)
      return
    }
    // Joiner with no recoverable identity: send them to rejoin with the code prefilled.
    // Wait for auth to settle first so a returning creator isn't misrouted.
    if (!authLoading) {
      navigate(`/?join=${session.roomCode}`)
    }
  }, [session, sessionId, navigate, user?.uid, participants, authLoading])

  // Subscribe to participants
  useEffect(() => {
    if (!sessionId) return
    const unsub = subscribeToParticipants(sessionId, setParticipants)
    return unsub
  }, [sessionId])

  async function handleCopyCode() {
    if (!session) return
    const link = `${window.location.origin}/?join=${session.roomCode}`
    await navigator.clipboard.writeText(`Join my Day One workshop: ${link}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleStart() {
    if (!sessionId) return
    setStarting(true)
    try {
      // If returning team, trigger summary generation before starting
      if (session?.isReturning && !summaryTriggered.current) {
        summaryTriggered.current = true
        fetch(`${FUNCTIONS_BASE_URL}/generateReturningSummary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({ sessionId }),
        }).catch(() => {}) // best-effort, don't block start
      }
      await startSession(sessionId)
    } catch {
      setStarting(false)
    }
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-stone flex items-center justify-center">
        <p className="mono-label text-navy/70">
          Loading the room
          <span className="caret" aria-hidden="true" />
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-stone">
      {/* Header */}
      <header className="bg-navy text-stone py-4 px-6 border-b-2 border-golden">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
          <h1 className="hl text-2xl text-stone">Day One</h1>
          <span className="mono-label text-golden truncate">{session.sessionName}</span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Returning Team Banner */}
        {session.isReturning && (
          <div className="card card-shadow bg-paper p-5 flex items-start gap-3">
            <RotateCcw className="w-5 h-5 text-amber shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="mono-label text-teal">Returning team</p>
              <p className="text-sm text-navy/75 mt-1">
                You have run this before. Scout picks up from your earlier work and skips the basics.
              </p>
            </div>
          </div>
        )}

        {/* Room Code Card */}
        <div className="card card-shadow p-6 sm:p-8">
          <p className="mono-label text-teal border-b-2 border-navy pb-2 mb-4">Room code</p>
          <div className="flex items-center gap-4 mb-3">
            <span data-testid="room-code" className="font-mono text-4xl sm:text-5xl font-medium tracking-[0.25em] text-navy select-all">
              {session.roomCode}
            </span>
            <button
              onClick={handleCopyCode}
              className="p-2 border-2 border-navy/55 hover:border-navy hover:bg-paper transition-colors text-navy cursor-pointer"
              aria-label="Copy the invite link"
            >
              {copied ? <Check className="w-5 h-5 text-teal" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <p className="text-navy/70 text-sm">
            Copy sends an invite link. Your team enters their name and the room password.
          </p>
        </div>

        {/* Participant List */}
        <div className="card card-shadow p-6">
          <div className="flex items-center gap-2 border-b-2 border-navy pb-2 mb-4">
            <Users className="w-4 h-4 text-navy/70" aria-hidden="true" />
            <h2 className="mono-label text-teal">Team members</h2>
            <span className="ml-auto mono-label text-navy/70 tabular-nums">
              {participants.length} joined
            </span>
          </div>
          {participants.length === 0 ? (
            <p className="mono-label text-navy/70 py-4">
              Waiting for the first person to join
              <span className="caret" aria-hidden="true" />
            </p>
          ) : (
            <ul className="space-y-3">
              {participants.map((p) => (
                <li key={p.id} className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-full border-2 border-navy flex items-center justify-center text-white text-sm font-semibold shrink-0"
                    style={{ backgroundColor: p.color }}
                  >
                    {p.displayName.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium text-navy">
                    {p.displayName}
                  </span>
                  {p.id === session.createdBy && (
                    <span className="mono-label text-amber-dark border border-amber px-1.5 py-0.5">
                      Host
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* What to Expect */}
        <div className="card card-shadow p-6">
          <h2 className="mono-label text-teal border-b-2 border-navy pb-2 mb-4">
            What to expect
          </h2>
          <ol>
            {([1, 2, 3, 4, 5] as Stage[]).map((stage) => (
              <li
                key={stage}
                className="flex items-baseline gap-4 py-2.5 border-b border-navy/15 last:border-0"
              >
                <span className="mono-label text-teal tabular-nums shrink-0">
                  0{stage}
                </span>
                <p className="font-display text-base text-navy flex-1">
                  {STAGE_NAMES[stage]}
                </p>
                <p className="mono-label text-navy/70">
                  {AGENT_DISPLAY_NAMES[STAGE_AGENTS[stage]]}
                </p>
              </li>
            ))}
          </ol>
        </div>

        {/* Start Button (creator only) */}
        {isCreator && (
          <div className="pt-2 pb-10">
            <button
              onClick={handleStart}
              disabled={starting || participants.length < 1}
              className="btn btn-amber"
            >
              <Play className="w-4 h-4" aria-hidden="true" />
              {starting ? 'Starting' : 'Start the workshop'}
            </button>
          </div>
        )}
      </main>
    </div>
  )
}
